import { getCellProps } from 'src/types';
import { invokeOrIdentity } from 'src/utils';
import arraysDoOverlap from 'src/utils/arrayOperations/arraysOverlap';
import ensureArray from 'src/utils/arrayOperations/ensureArray';
import getClassArrayFromHeterogeneousClassInput from './getClassArrayFromHeterogeneousClassInput';
import getCssPropertiesFromAmbiguousStyleInput from './getCssPropertiesFromAmbiguousStyleInput';
import getValue from './getValue';
const normalizeClasses = getClassArrayFromHeterogeneousClassInput;
const normalizeStyles = getCssPropertiesFromAmbiguousStyleInput;
const getClasses = (invokable, cellProps) => ensureArray(normalizeClasses(invokeOrIdentity(invokable, cellProps)));
const getStyles = (invokable, cellProps) => normalizeStyles(invokeOrIdentity(invokable, cellProps));
const isTotalsRow = (cellProps) => {
    var _a, _b;
    return ((_a = cellProps.row) === null || _a === void 0 ? void 0 : _a.isTotalsRow) || ((_b = cellProps.row) === null || _b === void 0 ? void 0 : _b.isGrandTotalsRow);
};
function getGroupPosition(allColumns, col) {
    var _a, _b, _c;
    if (!col)
        return undefined;
    const colIdx = allColumns.findIndex(c => c.name === col.name);
    const columnGroupMinus1 = (_a = allColumns[colIdx - 1]) === null || _a === void 0 ? void 0 : _a.columnGroup;
    const columnGroupPlus1 = (_b = allColumns[colIdx + 1]) === null || _b === void 0 ? void 0 : _b.columnGroup;
    const overlap0Minus1 = arraysDoOverlap(col.columnGroup, columnGroupMinus1);
    const overlap0Plus1 = arraysDoOverlap(col.columnGroup, columnGroupPlus1);
    const previousColGroupPosition = (_c = getGroupPosition(allColumns, allColumns[colIdx - 1])) === null || _c === void 0 ? void 0 : _c.position;
    if (col.columnGroup === undefined)
        return undefined;
    if (overlap0Minus1 && previousColGroupPosition !== 'last') {
        if (overlap0Plus1) {
            return { position: 'mid', cssClasses: 'padding-right-0 padding-left-0' };
        }
        else {
            return { position: 'last', cssClasses: 'padding-left-0' };
        }
    }
    else if (overlap0Plus1) {
        return { position: 'first', cssClasses: 'padding-right-0' };
    }
    else {
        return undefined;
    }
}
export function getThClasses(col) {
    const res = normalizeClasses(col.thClasses);
    if (col.alignHeader === 'right')
        res.push('align-right');
    if (col.alignHeader === 'left')
        res.push('align-left');
    if (col.alignHeader === 'center')
        res.push('align-center');
    if (col.name === 'row_menu')
        res.push('row_menu');
    if (col.isSectionHeader)
        res.push('table-section-header');
    return res;
}
export function getThStyles(col) {
    return normalizeStyles(col.thStyles);
}
export function getRowClasses(props, ctx) {
    const { row } = props;
    if (!row)
        return [];
    if (!props.row.id)
        return [];
    const res = [];
    if (row.isTotalsRow)
        res.push('totals-row');
    if (row.isGrandTotalsRow)
        res.push('grand-totals-row');
    if (ctx.rowObject && ctx.rowObject.classes) {
        const rowObject = ctx.rowObject;
        if (typeof rowObject.classes === 'function') {
            const calcClasses = rowObject.classes({ rowData: row, tableData: ctx.data, context: ctx });
            if (calcClasses)
                res.push(...calcClasses);
        }
        else if (Array.isArray(rowObject.classes) && typeof rowObject.classes[0] === 'string') {
            const classes = rowObject.classes;
            res.push(...classes);
        }
    }
    return res;
}
export function getRowStyles(props, ctx) {
    let res = {};
    if (ctx.rowObject && ctx.rowObject.styles) {
        const rowObject = ctx.rowObject;
        if (typeof rowObject.styles === 'function') {
            const styles = normalizeStyles(rowObject.styles({ rowData: props.row, tableData: ctx.data, context: ctx }));
            if (styles)
                res = Object.assign(Object.assign({}, res), styles);
        }
        else if (Array.isArray(rowObject.styles) && typeof rowObject.styles[0] === 'string') {
            const styles = normalizeStyles(rowObject.styles);
            if (styles)
                res = Object.assign(Object.assign({}, res), styles);
        }
        else if (typeof rowObject.styles === 'string') {
            res = Object.assign(Object.assign({}, res), normalizeStyles(rowObject.styles));
        }
    }
    return res;
}
export function getCellContentStyles(cellProps) {
    return getStyles(cellProps.col.cellContentStyles, cellProps);
}
export function getCellContentClasses(col, props, allColumns) {
    const cellProps = getCellProps(Object.assign(Object.assign({}, props), { col }));
    const res = getClasses(col.cellContentClasses, cellProps);
    const groupPosition = getGroupPosition(allColumns, col);
    if (groupPosition === null || groupPosition === void 0 ? void 0 : groupPosition.position) {
        res.push('column-group-member ' + groupPosition.position);
    }
    return res;
}
export function getTdStyles(col, props) {
    let res;
    const cellProps = getCellProps(Object.assign(Object.assign({}, props), { col }));
    res = getStyles(col.tdStyles, cellProps);
    if (!isTotalsRow(cellProps)) {
        if (col.name === 'row_menu')
            res = Object.assign(Object.assign({}, res), { width: '25px' });
        if (col.isButton)
            res = Object.assign(Object.assign({}, res), { width: '30px' });
    }
    return res;
}
export function getTdClasses(col, props, ctx, allColumns) {
    const cellProps = Object.assign(Object.assign({}, props), { col });
    let res = [];
    if (isTotalsRow(cellProps)) {
        res = [...res,
            ...ensureArray(invokeOrIdentity(col.grandTotalsRowClasses, cellProps))
                .filter(x => x !== undefined)
                .map(x => String(x))];
    }
    else {
        res = [...res, ...normalizeClasses(invokeOrIdentity(col.tdClasses, cellProps))];
        if (col.isIcon)
            res.push('row-button');
        if (col.cellClickHandler)
            res.push('cursor-pointer');
        if (col.name === 'row_menu')
            res.push('row_menu');
        if (col.isSectionHeader)
            res.push('table-section-cell');
    }
    if (ctx.doDisguise && col.disguised)
        res.push('blurry-text');
    const groupPosition = getGroupPosition(allColumns, col);
    if (groupPosition === null || groupPosition === void 0 ? void 0 : groupPosition.cssClasses) {
        res = [...res, ...ensureArray(groupPosition.cssClasses)];
    }
    return res;
}
export function getCellDivClasses(col, props) {
    const cellProps = getCellProps(Object.assign(Object.assign({}, props), { col }));
    const res = getClasses(col.cellDivClasses, cellProps);
    if (!isTotalsRow(cellProps)) {
        if (col.cellLeftClickMenu) {
            res.push('text-action-link');
        }
    }
    if (col.align === 'right')
        res.push('align-right');
    if (col.align === 'left')
        res.push('align-left');
    if (col.align === 'center')
        res.push('align-center');
    return res;
}
export function getCellDivStyles(col, props, ctx) {
    const cellProps = getCellProps(Object.assign(Object.assign({}, props), { col }));
    if (isTotalsRow(cellProps) || col.name === 'row_menu')
        return {};
    let res = getStyles(col.cellDivStyles, cellProps);
    if (col.maxLines) {
        const obj = {
            'overflow': 'hidden',
            'text-overflow': 'ellipsis',
            'display': '-webkit-box',
            '-webkit-box-orient': 'vertical',
            '-webkit-line-clamp': String(col.maxLines) /* number of lines to show */
        };
        res = Object.assign(Object.assign({}, res), normalizeStyles(obj));
    }
    if (ctx.cellIsEditable(Object.assign(Object.assign({}, props), { col })))
        res = (Object.assign(Object.assign({}, res), { cursor: 'pointer' }));
    if (col.isDate || col.isTime)
        res = (Object.assign(Object.assign({}, res), { whiteSpace: 'nowrap' }));
    const value = getValue(col, props.row);
    if (value === undefined) {
        // console.warn('value undefined. possible error')
        return {};
    }
    const valueStr = value === null ? 'null' : String(value);
    const labelColorInfoExists = (col.labelColorMap && (col.labelColorMap[valueStr] || col.labelColorMap.default)) ||
        col.labelBackgroundColor !== undefined;
    if (labelColorInfoExists) {
        res = Object.assign(Object.assign({}, res), { 'padding': '0 6px 0 6px' });
        res = Object.assign(Object.assign({}, res), { 'borderRadius': '5px' });
        res = Object.assign(Object.assign({}, res), { 'color': 'black' });
        res = Object.assign(Object.assign({}, res), { width: 'fit-content' });
        if (col.labelColorMap && (col.labelColorMap[valueStr] || col.labelColorMap.default)) {
            res = Object.assign(Object.assign({}, res), { 'backgroundColor': col.labelColorMap[valueStr] || col.labelColorMap.default });
        }
        if (col.labelBackgroundColor !== undefined) {
            res = Object.assign(Object.assign({}, res), { 'backgroundColor': col.labelBackgroundColor(cellProps) });
        }
    }
    return res;
}
